import { useCallback } from 'react';
import { DNI_CONFIG } from '@/lib/dni/config';
import { useDNI } from '@/hooks/useDNI';
import { trackClickCall } from '@/lib/dni/trackingEvents';

interface CallRailPhoneProps {
  /** Display format - 'formatted' shows (469) 404-0899, 'raw' shows 469-404-0899 */
  format?: 'formatted' | 'raw';
  /** Additional CSS classes */
  className?: string;
  /** Render as link (tap-to-call) or just text */
  asLink?: boolean;
  /** For tracking which component triggered the call */
  source?: string;
  /** Children to render (optional, overrides number display) */
  children?: React.ReactNode;
}

/**
 * CallRailPhone Component
 * 
 * Renders a phone number with DNI (Dynamic Number Insertion) support.
 * 
 * When CallRail is enabled:
 * - Renders with .callrail-phone class and data attributes
 * - CallRail's script will automatically swap the number
 * 
 * When Twilio router is enabled:
 * - Uses the useDNI hook to get the assigned number
 * - Swaps are handled client-side
 * 
 * SEO Safety:
 * - Default number always renders in HTML (before JS runs)
 * - Number swapping happens after page load
 */
export const CallRailPhone = ({
  format = 'raw',
  className = '',
  asLink = true,
  source = 'unknown',
  children,
}: CallRailPhoneProps) => {
  const { context, getDisplayNumber, getTelHref, assignedNumber } = useDNI();

  const handleClick = useCallback(() => {
    trackClickCall(context, source, assignedNumber);
  }, [context, source, assignedNumber]);

  // Format the display number
  const displayNumber = getDisplayNumber(format);
  const telHref = getTelHref();

  // Data attributes for CallRail and analytics
  const dataAttributes = {
    'data-phone-swap': 'true',
    'data-tel': 'main',
    'data-source': source,
  };

  if (asLink) {
    return (
      <a
        href={telHref}
        className={`callrail-phone ${className}`}
        onClick={handleClick}
        aria-label="Call us"
        {...dataAttributes}
      >
        {children || displayNumber}
      </a>
    );
  }

  return (
    <span 
      className={`callrail-phone ${className}`}
      {...dataAttributes}
    >
      {children || displayNumber}
    </span>
  );
};

/**
 * Simplified phone link for inline use
 * Always renders with DNI selectors
 */
export const PhoneLink = ({ 
  className = '', 
  source = 'inline',
  children 
}: { 
  className?: string; 
  source?: string;
  children?: React.ReactNode;
}) => {
  return (
    <CallRailPhone
      asLink={true}
      format="raw"
      className={className}
      source={source}
    >
      {children}
    </CallRailPhone>
  );
};
